import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { trackedFunction } from 'reactiveweb/function';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { not } from 'tio-ui/utilities';
import Table from 'tio-ui/components/table';
import Select from 'tio-ui/components/select';
import Button from 'tio-ui/components/button';
import {
  PhCaretLeft,
  PhCaretRight,
  PhWarning,
  PhArrowUp,
  PhArrowDown,
  PhInfo,
} from 'ember-phosphor-icons';
import {
  buildAggregationQuery,
  buildCompositeAggregationQuery,
} from '../../utils/aggregation-query-builder';
import {
  flattenAggBuckets,
  flattenCompositeBuckets,
  sortAggregationRows,
} from '../../utils/aggregation-response-parser';
import { sortsToStrings, findSortIndex, type SortEntry } from '../../utils/sort-helpers';
import type { ColumnAggregationMap, DateGroupingMap } from '../../utils/aggregation-types';
import {
  applyColumnFormat,
  buildUrlValue,
  getDefaultDateFormat,
  getDefaultFormatForField,
} from '../../utils/column-format-options';
import { buildReportQueryCore } from '../../utils/report-search-body';
import type {
  ColumnFormatMap,
  UrlFormatConfig,
  AppContext,
} from '../../utils/column-format-options';
import { type FilterLayout } from '../../utils/filter-operators';
import { searchRequestFor } from '../../utils/search-requests';
import { isSqlDataset } from '../../utils/dataset-utils';
import type { Store as StoreService } from '@warp-drive/core';
import type { FieldOption } from '../../utils/opensearch-field-mapper';

interface OpenSearchHit {
  _source: Record<string, unknown>;
}

type CompanyFilterOperator = 'in' | 'not_in';

const SNAKE_TYPED_SUFFIX_RE = /_(keyword|text|date|integer|float|boolean)$/;
const CAMEL_TYPED_SUFFIX_RE = /(Keyword|Text|Date|Integer|Float|Boolean)$/;

function snakeToCamelKey(key: string): string {
  return key.replace(/_([a-z0-9])/g, (_, ch: string) => ch.toUpperCase());
}

function unique(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))];
}

function segmentCandidates(segment: string, isLeaf: boolean): string[] {
  const camel = snakeToCamelKey(segment);
  const candidates = [segment, camel];

  if (isLeaf) {
    const snakeNoSuffix = segment.replace(SNAKE_TYPED_SUFFIX_RE, '');
    const camelNoSuffix = camel.replace(CAMEL_TYPED_SUFFIX_RE, '');
    candidates.push(snakeNoSuffix, snakeToCamelKey(snakeNoSuffix), camelNoSuffix);
  }

  return unique(candidates);
}

function resolvePathWithVariants(row: Record<string, unknown>, key: string): unknown {
  // If the full key exists as a flat property, use it directly.
  // This handles aggregation rows where dot-containing keys like
  // "employee_ef_fields.employee_email_keyword" are flat properties.
  if (key in row) {
    return row[key];
  }

  const segments = key.split('.').filter(Boolean);
  let current: unknown = row;

  for (let index = 0; index < segments.length; index++) {
    if (current == null || typeof current !== 'object') return undefined;

    // If current is an Array, take the first element
    if (Array.isArray(current)) {
      current = current[0];
      if (current == null || typeof current !== 'object') return undefined;
    }

    const objectCurrent = current as Record<string, unknown>;
    const isLeaf = index === segments.length - 1;
    const segment = segments[index] as string;
    const match = segmentCandidates(segment, isLeaf).find(
      (candidate) => candidate in objectCurrent
    );
    if (!match) return undefined;
    current = objectCurrent[match];
  }

  return current;
}

/**
 * Resolve a column's raw (unformatted) value from a row, preferring the
 * backend `resultKey` (aggregation/alias key) and falling back to the
 * column `key`. Shared by every formatting path in `getCellValue` /
 * `getUrlLink` so key-resolution stays consistent across them.
 */
function resolveRawValue(
  row: Record<string, unknown>,
  col: Pick<FieldOption, 'key' | 'resultKey'>
): unknown {
  const rawKey = col.resultKey || col.key;
  const raw = resolvePathWithVariants(row, rawKey);
  if (raw === undefined && col.resultKey) {
    return resolvePathWithVariants(row, col.key);
  }
  return raw;
}

/**
 * Resolve a cell value from a row object, supporting dot-path keys for
 * nested OpenSearch _source objects (e.g. "employee_ef_fields.employee_email_keyword").
 * When a path segment resolves to an Array, the first element is used.
 */
export function resolveCellValue(row: Record<string, unknown>, key: string): string {
  if (row == null || typeof row !== 'object') return '—';
  let value: unknown = resolvePathWithVariants(row, key);

  if (value === undefined && !key.includes('.')) {
    const fallbackKey = `${key}.value`;
    value = resolvePathWithVariants(row, fallbackKey);
  }

  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) {
    const displayable = value
      .filter((v) => v != null)
      .map((v) => {
        if (typeof v === 'object') {
          const obj = v as Record<string, unknown>;
          // Prefer a human-readable property; fall back to a length-capped
          // JSON serialization so verbose nested objects don't blow out the
          // table cell or leak "[object Object]" to the user.
          const candidate = obj.label ?? obj.name ?? obj.value ?? obj.id;
          if (candidate != null) return String(candidate);
          const json = JSON.stringify(obj);
          return json.length > 80 ? `${json.slice(0, 77)}…` : json;
        }
        return String(v);
      });
    return displayable.length > 0 ? displayable.join(', ') : '—';
  }
  if (value instanceof Date) return value.toLocaleDateString();
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

interface OpenSearchResponse {
  hits?: {
    hits?: OpenSearchHit[];
    total?: { value?: number } | number;
  };
  aggregations?: Record<string, unknown>;
}

export interface PreviewTableSignature {
  Args: {
    columns: FieldOption[];
    allFields?: FieldOption[];
    /** User filters — always combined as flat AND. */
    filterValues: Record<string, unknown>;
    /** Build filters keyed by field. Combined per `buildFilterLayout` when present (top-level AND with optional OR groups); when the layout is omitted, build filters are AND'd flatly. */
    buildFilterValues?: Record<string, unknown>;
    /** Layout for build filters. When omitted, build filters are AND'd flatly (backwards-compat with legacy callers that haven't been updated). */
    buildFilterLayout?: FilterLayout;
    companyId: string | undefined;
    companyIds?: string[];
    companyFilterOperator?: CompanyFilterOperator;
    companyFilterField?: string;
    modelType: string;
    sorts?: SortEntry[];
    /** @deprecated Use sorts instead — kept for backwards compatibility with report-view */
    sortField?: string;
    page: number;
    limit: number;
    onSort: (sortField: string) => void;
    onAddSort?: (key: string, direction: 'asc' | 'desc') => void;
    onToggleDirection?: (key: string) => void;
    onPageChange: (page: number) => void;
    onLimitChange: (limit: number) => void;
    readOnly?: boolean;
    /** Hide the "Report Preview" heading row (embedded contexts, e.g. dashboard tiles). */
    hideHeading?: boolean;
    isAggregated?: boolean;
    groupByFieldKeys?: string[];
    columnAggregationMap?: ColumnAggregationMap;
    dateGroupingIntervals?: DateGroupingMap;
    columnFormatMap?: ColumnFormatMap;
    columnLabelMap?: Record<string, string>;
    columnAltTextMap?: Record<string, string>;
    appContext?: AppContext;
  };
}

class PreviewTable extends Component<PreviewTableSignature> {
  @service declare store: StoreService;

  // Dedup key to avoid refiring identical queries
  private lastSearchKey?: string;
  private lastSearchPromise?: Promise<{ hits: Record<string, unknown>[]; total: number }>;
  /** True when the grouped preview hit its bounded fetch cap (more groups exist). */
  @tracked aggResultTruncated = false;

  get effectiveSorts(): SortEntry[] {
    if (this.args.sorts && this.args.sorts.length > 0) return this.args.sorts;
    if (this.args.sortField) {
      const field = this.args.sortField.replace(/:(?:asc|desc)$/, '');
      const direction: 'asc' | 'desc' = this.args.sortField.endsWith(':desc') ? 'desc' : 'asc';
      return [{ key: field, direction }];
    }
    return [];
  }

  get hasColumns() {
    return this.args.columns.length > 0;
  }

  get columnKeys() {
    return this.args.columns.map((c) => c.key);
  }

  // Build and execute the OpenSearch query reactively
  // Matches the same adapter.search() pattern used by programs-data-provider
  searchData = trackedFunction(this, async () => {
    if (!this.hasColumns) return { hits: [], total: 0 };

    // Eagerly consume all tracked args so auto-tracking registers them
    // as dependencies even when they're only used in conditional branches.
    const dateGroupingIntervals = this.args.dateGroupingIntervals;

    // Company scoping + build/user filter clauses + group-by HAVING split —
    // shared with the persisted searchBody and the viewer export so all three
    // surfaces run the same query semantics.
    const {
      builder,
      having,
      isAggregated: isAgg,
    } = buildReportQueryCore({
      modelType: this.args.modelType,
      fields: this.args.allFields ?? this.args.columns,
      buildFilterValues: this.args.buildFilterValues,
      buildFilterLayout: this.args.buildFilterLayout,
      userFilterValues: this.args.filterValues,
      companyId: this.args.companyId,
      companyIds: this.args.companyIds,
      companyFilterField: this.args.companyFilterField,
      companyFilterOperator: this.args.companyFilterOperator,
      isAggregated: this.args.isAggregated,
      groupByFieldKeys: this.groupByKeys,
      columns: this.args.columns,
    });
    // Preview tables render full-cell values from _source; highlighting is unused.
    // Disable default wildcard highlight to keep request payload focused.
    builder.setHighlight([]);

    // Aggregated mode: composite aggregation with bounded pagination.
    //
    // Nested `terms` aggs multiply per level and blow past OpenSearch's
    // search.max_buckets (65,535) once several group-by columns combine. A
    // composite aggregation returns one flat page of buckets at a time and
    // paginates via after_key, so grouping never 503s regardless of cardinality.
    // For the preview we page up to `compositePreviewCap` groups, then sort
    // client-side — composite can only order by its group-key sources, not by a
    // metric, so metric-column sorts rank within the fetched window.
    if (isAgg) {
      const compositePreviewCap = 5000;
      // Hard ceiling on requests for the interactive preview. A HAVING
      // bucket_selector can filter most buckets out of each page, so we can't
      // assume the cap is reached in ~cap/page-size pages — bound the request count.
      const maxCompositePages = 20;
      const baseBody = { ...builder.build(), size: 0 };

      const requestKey = JSON.stringify({
        modelType: this.args.modelType,
        baseBody,
        groupByKeys: this.groupByKeys,
        aggMap: this.aggMap,
        sorts: this.effectiveSorts,
        dateGroupingIntervals,
        having: having.selectors,
      });
      if (this.lastSearchKey === requestKey && this.lastSearchPromise) {
        return this.lastSearchPromise;
      }

      this.lastSearchKey = requestKey;
      // SQL datasets: single nested group_by request. The composite paging
      // below works around OpenSearch's search.max_buckets (65,535) — a limit
      // SQL datasets don't have (Postgres GROUP BY, bounded by the dataset's
      // row_cap) — and the sql-search translator's grammar is the nested
      // terms/date_histogram shape, not `composite`.
      if (isSqlDataset(this.args.modelType)) {
        const aggQuery = buildAggregationQuery(
          this.groupByKeys,
          this.args.columns,
          this.aggMap,
          this.effectiveSorts,
          dateGroupingIntervals,
          having.selectors
        );
        this.lastSearchPromise = this.store
          .request(
            searchRequestFor(
              { 'model-type': this.args.modelType, searchBody: { ...baseBody, aggs: aggQuery } },
              this.args.companyId
            )
          )
          .then(({ content }) => {
            const data = content as OpenSearchResponse;
            const allRows = flattenAggBuckets(
              data?.aggregations || {},
              this.groupByKeys,
              this.aggMap
            );
            // Same preview window as the composite path: the server bounds
            // buckets by the dataset row_cap (which authors may raise), and
            // rendering tens of thousands of rows hangs the preview.
            const truncated = allRows.length > compositePreviewCap;
            const windowRows = truncated ? allRows.slice(0, compositePreviewCap) : allRows;
            this.aggResultTruncated = truncated;
            return { hits: windowRows, total: windowRows.length };
          });

        try {
          return await this.lastSearchPromise;
        } catch {
          if (this.lastSearchKey === requestKey) {
            this.lastSearchPromise = undefined;
          }
          // Clear any truncation banner left by a prior composite preview —
          // an empty error result must not render "showing first N groups".
          this.aggResultTruncated = false;
          return { hits: [], total: 0 };
        }
      }

      // WarpDrive wave 1.5: execute via the source-kind seam; unwrap `.content`.
      this.lastSearchPromise = (async () => {
        const allRows: Record<string, unknown>[] = [];
        let after: Record<string, unknown> | undefined;
        let more = false; // groups remain unfetched when we stop (cap or page ceiling)

        // Page through composite results until the last page or the preview cap.
        // The page ceiling is a hard safety net; the cap normally breaks first.
        for (let page = 0; page < maxCompositePages; page++) {
          const aggQuery = buildCompositeAggregationQuery(
            this.groupByKeys,
            this.args.columns,
            this.aggMap,
            this.effectiveSorts,
            dateGroupingIntervals,
            having.selectors,
            after
          );
          const { content } = await this.store.request(
            searchRequestFor(
              {
                'model-type': this.args.modelType,
                searchBody: { ...baseBody, aggs: aggQuery },
              },
              this.args.companyId
            )
          );
          const data = content as OpenSearchResponse;
          const { rows, afterKey } = flattenCompositeBuckets(
            data?.aggregations || {},
            this.groupByKeys,
            this.aggMap
          );
          allRows.push(...rows);

          // The only reliable end-of-results signal is a missing after_key. A
          // HAVING bucket_selector can filter a page below COMPOSITE_PAGE_SIZE
          // while more pages still exist, so bucket count must NOT drive
          // termination — only after_key (plus the cap / page ceiling) may.
          if (!afterKey) {
            more = false;
            break;
          }
          if (allRows.length >= compositePreviewCap) {
            more = true;
            break;
          }
          after = afterKey;
          more = true; // after_key present; more remain if the loop ends here
        }

        // Trim to the preview window. Truncated when more groups exist than we
        // show — either we stopped at the cap, or the last page overshot it — so
        // `total` (and the "showing first N" notice) never exceeds the cap.
        const truncated = more || allRows.length > compositePreviewCap;
        const windowRows = truncated ? allRows.slice(0, compositePreviewCap) : allRows;
        this.aggResultTruncated = truncated;
        // Resolve each sort's key to the row key (col.key) it lands on, so a
        // metric-column sort — which composite cannot do server-side — actually
        // reorders the fetched rows here, whether the sort was keyed by col.key,
        // path, or resultKey.
        const rowKeySorts = this.effectiveSorts.map((s) => {
          const col = this.args.columns.find((c) =>
            [c.sortPath, c.path, c.key, c.resultKey].includes(s.key)
          );
          return { key: col?.key ?? s.key, direction: s.direction };
        });
        const sorted = sortAggregationRows(windowRows, rowKeySorts);
        return { hits: sorted, total: sorted.length };
      })();

      try {
        return await this.lastSearchPromise;
      } catch {
        if (this.lastSearchKey === requestKey) {
          this.lastSearchPromise = undefined;
        }
        this.aggResultTruncated = false;
        return { hits: [], total: 0 };
      }
    }

    // Standard hits mode: pagination, sort, column projection
    builder.setPagination(this.args.page, this.args.limit);

    // Sort
    if (this.effectiveSorts.length > 0) {
      builder.setSort(sortsToStrings(this.effectiveSorts));
    } else {
      builder.setSort('_score');
    }

    // Return only the fields needed by the visible report columns.
    // Include both canonical path and key fallback so legacy column configs
    // still resolve even when one representation is unavailable.
    const selectedSourcePaths = this.args.columns
      .flatMap((col) => [col.path, col.key])
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
    builder.selectSourceFields(selectedSourcePaths);

    const searchBody = builder.build();

    // Build the request payload matching the adapter's expected format
    const requestPayload = {
      'model-type': this.args.modelType,
      searchBody,
    };

    // Request dedup (same pattern as programs-data-provider)
    const requestKey = JSON.stringify(requestPayload);
    if (this.lastSearchKey === requestKey && this.lastSearchPromise) {
      return this.lastSearchPromise;
    }

    this.lastSearchKey = requestKey;
    // WarpDrive wave 1.5: execute via the shared search builder (replaces
    // `store.adapterFor('search').search`); unwrap `.content`.
    this.lastSearchPromise = this.store
      .request(searchRequestFor(requestPayload, this.args.companyId))
      .then(({ content }) => {
        const data = content as OpenSearchResponse;
        const hits = (data?.hits?.hits || []).map((hit: OpenSearchHit) => hit._source);
        const rawTotal = data?.hits?.total;
        const total = typeof rawTotal === 'number' ? rawTotal : (rawTotal?.value ?? 0);
        return { hits, total };
      });

    try {
      return await this.lastSearchPromise;
    } catch {
      if (this.lastSearchKey === requestKey) {
        this.lastSearchPromise = undefined;
      }
      return { hits: [], total: 0 };
    }
  });

  get groupByKeys() {
    return this.args.groupByFieldKeys || [];
  }

  get aggMap() {
    return this.args.columnAggregationMap || {};
  }

  get rows() {
    const allRows = this.searchData.value?.hits || [];
    // Client-side pagination for aggregated mode
    if (this.args.isAggregated) {
      const start = (this.args.page - 1) * this.args.limit;
      return allRows.slice(start, start + this.args.limit);
    }
    return allRows;
  }

  get total() {
    return this.searchData.value?.total || 0;
  }

  get isLoading() {
    return this.searchData.isLoading;
  }

  get totalPages() {
    return Math.max(1, Math.ceil(this.total / this.args.limit));
  }

  get canGoBack() {
    return this.args.page > 1;
  }

  get canGoForward() {
    return this.args.page < this.effectiveTotalPages;
  }

  /** OpenSearch offset pagination is limited to 10,000 results (from + size) */
  get isNearOffsetLimit() {
    // Aggregated mode pages composite results into a bounded preview window;
    // reuse this warning slot when that window was truncated.
    if (this.args.isAggregated) return this.aggResultTruncated;
    return this.total > 10_000;
  }

  get offsetLimitWarning() {
    if (!this.isNearOffsetLimit) return '';
    if (this.args.isAggregated) {
      return `Showing the first ${this.total.toLocaleString()} groups. Add filters to narrow the report, or use Export CSV for the complete set.`;
    }
    return `Showing first 10,000 of ${this.total.toLocaleString()} results. Use Export CSV to download all data.`;
  }

  /** Clamp totalPages so we never request beyond the 10K offset window */
  get effectiveTotalPages() {
    if (this.isNearOffsetLimit) {
      return Math.min(this.totalPages, Math.floor(10_000 / this.args.limit));
    }
    return this.totalPages;
  }

  getCellValue = (row: Record<string, unknown>, col: FieldOption) => {
    // Resolve the raw value for potential formatting
    const formatConfig = this.args.columnFormatMap?.[col.key];
    if (formatConfig) {
      // URL format rendering is handled separately in getUrlLink
      if (formatConfig.type === 'url') {
        const raw = resolveRawValue(row, col);
        if (raw === null || raw === undefined || raw === '') return '—';
        return String(raw);
      }

      const raw = resolveRawValue(row, col);
      if (raw != null) {
        const groupingInterval = this.args.dateGroupingIntervals?.[col.key];
        const formatted = applyColumnFormat(raw, formatConfig, groupingInterval);
        if (formatted != null) return formatted;
      }
    }

    // Grouped date column without explicit format — apply interval formatting
    const groupingInterval = this.args.dateGroupingIntervals?.[col.key];
    if (groupingInterval) {
      const raw = resolveRawValue(row, col);
      if (raw != null) {
        const formatted = applyColumnFormat(raw, getDefaultDateFormat(), groupingInterval);
        if (formatted != null) return formatted;
      }
    }

    // No explicit format: apply a sensible default for currency-in-cents
    // columns (backend flags these via currencyCents/displayType). Otherwise
    // they'd render as raw integer cents (e.g. 13340 instead of $133.40).
    const defaultFormat = getDefaultFormatForField(col);
    if (defaultFormat) {
      const raw = resolveRawValue(row, col);
      if (raw != null) {
        const formatted = applyColumnFormat(raw, defaultFormat);
        if (formatted != null) return formatted;
      }
    }

    // Fallback: standard cell resolution
    if (col.resultKey) {
      const value = resolveCellValue(row, col.resultKey);
      if (value !== '—') return value;
    }
    return resolveCellValue(row, col.key);
  };

  /**
   * Get URL link data for a cell if the column has URL formatting.
   * Returns { href, label } or null.
   */
  getUrlLink = (row: Record<string, unknown>, col: FieldOption) => {
    const formatConfig = this.args.columnFormatMap?.[col.key];
    if (!formatConfig || formatConfig.type !== 'url') return null;
    const raw = resolveRawValue(row, col);
    return buildUrlValue(raw, formatConfig as UrlFormatConfig, this.args.appContext);
  };

  isUrlColumn = (col: FieldOption) => {
    return this.args.columnFormatMap?.[col.key]?.type === 'url';
  };

  handleSort = (field: string, event: MouseEvent) => {
    const existing = this.effectiveSorts.find((s) => s.key === field);
    if (existing) {
      // Multi-sort: toggle this entry's direction in place to preserve others
      if (this.effectiveSorts.length > 1 && this.args.onToggleDirection) {
        this.args.onToggleDirection(field);
      } else if (existing.direction === 'asc') {
        this.args.onSort(`${field}:desc`);
      } else {
        this.args.onSort(`${field}:asc`);
      }
    } else if (this.effectiveSorts.length > 0 && this.args.onAddSort) {
      this.openSortActionMenu(field, event);
    } else {
      this.args.onSort(`${field}:asc`);
    }
  };

  openSortActionMenu = (field: string, event: MouseEvent) => {
    const button = event.currentTarget as HTMLElement;
    const rect = button.getBoundingClientRect();
    this.sortMenuPosition = { top: rect.bottom + 4, left: rect.left };
    this.sortActionMenuKey = field;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-sort-action-menu]')) {
        this.dismissSortActionMenu();
      }
    };
    setTimeout(() => {
      if (this.sortActionMenuKey !== field) return;
      document.addEventListener('click', handler, { once: true });
      this.sortActionCleanup = () => document.removeEventListener('click', handler);
    }, 0);
  };

  dismissSortActionMenu = () => {
    this.sortActionMenuKey = null;
    this.sortMenuPosition = null;
    this.sortActionCleanup?.();
    this.sortActionCleanup = null;
  };

  replaceSort = (field: string) => {
    this.dismissSortActionMenu();
    this.args.onSort(`${field}:asc`);
  };

  addToSort = (field: string) => {
    this.dismissSortActionMenu();
    this.args.onAddSort?.(field, 'asc');
  };

  get sortMenuStyle() {
    if (!this.sortMenuPosition) return '';
    return `position:fixed;top:${this.sortMenuPosition.top}px;left:${this.sortMenuPosition.left}px;z-index:9999`;
  }

  getSortDirection = (sortPath: string) => {
    const entry = this.effectiveSorts.find((s) => s.key === sortPath);
    if (!entry) return '';
    return entry.direction;
  };

  getSortPosition = (sortPath: string): number => {
    return findSortIndex(this.effectiveSorts, sortPath);
  };

  get hasMultipleSorts() {
    return this.effectiveSorts.length > 1;
  }

  getColumnLabel = (key: string) => {
    return this.args.columnLabelMap?.[key] || '';
  };

  getColumnAltText = (key: string) => {
    return this.args.columnAltTextMap?.[key] || '';
  };

  @tracked sortActionMenuKey: string | null = null;
  @tracked sortMenuPosition: { top: number; left: number } | null = null;
  private sortActionCleanup: (() => void) | null = null;

  @tracked altTextVisibleKey: string | null = null;
  private altTextCleanup: (() => void) | null = null;

  toggleAltText = (key: string) => {
    if (this.altTextVisibleKey === key) {
      this.dismissAltText();
    } else {
      this.altTextVisibleKey = key;
      // Defer so this click doesn't immediately dismiss
      requestAnimationFrame(() => {
        // Guard: if popover was closed before rAF ran, bail out
        if (this.altTextVisibleKey !== key) return;
        const handler = (e: MouseEvent) => {
          const target = e.target as HTMLElement;
          if (!target.closest('[data-alt-text-popover]')) {
            this.dismissAltText();
          }
        };
        document.addEventListener('click', handler, { once: true });
        this.altTextCleanup = () => document.removeEventListener('click', handler);
      });
    }
  };

  private dismissAltText = () => {
    this.altTextVisibleKey = null;
    this.altTextCleanup?.();
    this.altTextCleanup = null;
  };

  isAltTextVisible = (key: string) => {
    return this.altTextVisibleKey === key;
  };

  goToPrevPage = () => {
    if (this.canGoBack) {
      this.args.onPageChange(this.args.page - 1);
    }
  };

  goToNextPage = () => {
    if (this.canGoForward) {
      this.args.onPageChange(this.args.page + 1);
    }
  };

  handleLimitChange = (key: string) => {
    this.args.onLimitChange(Number(key));
  };

  get limitOptions() {
    return [
      { key: '10', label: '10 per page' },
      { key: '25', label: '25 per page' },
      { key: '50', label: '50 per page' },
      { key: '100', label: '100 per page' },
    ];
  }

  get selectedLimitKey() {
    return String(this.args.limit);
  }

  sortPositionLabel = (sortPath: string): string => {
    const pos = this.getSortPosition(sortPath);
    return pos >= 0 ? String(pos + 1) : '';
  };

  <template>
    <div class="tio-bg-surface rounded-lg border tio-border-default">
      {{#unless @hideHeading}}
        <div class="tio-row-tight px-4 py-3 border-b tio-border-default">
          <span class="tio-text-sm-semibold tio-fg-secondary">📋 Report Preview</span>
        </div>
      {{/unless}}

      {{#if this.hasColumns}}
        <div class="overflow-x-auto">
          <Table @isLoading={{this.isLoading}} @stickyHeader={{true}}>
            <:thead as |t|>
              <t.tr>
                {{#each @columns as |col|}}
                  <t.th>
                    <div class="group tio-row-extra-tight">
                      <span>{{if
                          (this.getColumnLabel col.key)
                          (this.getColumnLabel col.key)
                          col.label
                        }}</span>
                      {{#if (this.getColumnAltText col.key)}}
                        <span class="relative">
                          <button
                            type="button"
                            aria-label="Column info"
                            class="inline-flex tio-fg-info hover:tio-fg-info-h"
                            {{on "click" (fn this.toggleAltText col.key)}}
                          >
                            <PhInfo @size={{14}} />
                          </button>
                          {{#if (this.isAltTextVisible col.key)}}
                            <div
                              data-alt-text-popover
                              class="app-popover absolute left-0 top-full z-50 mt-1 w-56 p-2 text-xs tio-fg-secondary"
                            >
                              {{this.getColumnAltText col.key}}
                            </div>
                          {{/if}}
                        </span>
                      {{/if}}
                      {{#if col.sortPath}}
                        {{#if (eq (this.getSortDirection col.sortPath) "asc")}}
                          <button
                            type="button"
                            aria-label="Sort {{col.label}} descending"
                            aria-pressed="true"
                            class="tio-report-sort-direction--active tio-inline-row-center gap-0.5 rounded-md tio-bg-success-soft p-1 tio-fg-success-body"
                            {{on "click" (fn this.handleSort col.sortPath)}}
                          >
                            {{#if this.hasMultipleSorts}}
                              <span
                                class="tio-text-micro font-bold leading-none"
                              >{{this.sortPositionLabel col.sortPath}}</span>
                            {{/if}}
                            <PhArrowUp @size={{14}} @weight="bold" />
                          </button>
                        {{else if (eq (this.getSortDirection col.sortPath) "desc")}}
                          <button
                            type="button"
                            aria-label="Sort {{col.label}} ascending"
                            aria-pressed="true"
                            class="tio-report-sort-direction--active tio-inline-row-center gap-0.5 rounded-md tio-bg-success-soft p-1 tio-fg-success-body"
                            {{on "click" (fn this.handleSort col.sortPath)}}
                          >
                            {{#if this.hasMultipleSorts}}
                              <span
                                class="tio-text-micro font-bold leading-none"
                              >{{this.sortPositionLabel col.sortPath}}</span>
                            {{/if}}
                            <PhArrowDown @size={{14}} @weight="bold" />
                          </button>
                        {{else}}
                          <button
                            type="button"
                            aria-label="Sort {{col.label}} ascending"
                            class="p-0.5 tio-fg-muted hover:tio-fg-brand transition-colors"
                            {{on "click" (fn this.handleSort col.sortPath)}}
                          >
                            <PhArrowUp @size={{14}} />
                          </button>
                        {{/if}}
                      {{/if}}
                    </div>
                  </t.th>
                {{/each}}
              </t.tr>
            </:thead>
            <:tbody as |t|>
              {{#each this.rows as |row|}}
                <t.tr>
                  {{#each @columns as |col|}}
                    <t.td>
                      {{#let (this.getUrlLink row col) as |urlLink|}}
                        {{#if urlLink}}
                          <a
                            href={{urlLink.href}}
                            class="tio-fg-brand hover:tio-fg-brand underline"
                            target="_blank"
                            rel="noopener noreferrer"
                          >{{urlLink.label}}</a>
                        {{else}}
                          {{this.getCellValue row col}}
                        {{/if}}
                      {{/let}}
                    </t.td>
                  {{/each}}
                </t.tr>
              {{/each}}
              {{#if (eq this.rows.length 0)}}
                <t.tr>
                  <t.td>
                    <div class="text-center tio-fg-muted py-8 col-span-full">
                      No data found
                    </div>
                  </t.td>
                </t.tr>
              {{/if}}
            </:tbody>
          </Table>
        </div>

        {{! Pagination }}
        <div class="tio-row-between px-4 py-3 border-t tio-border-default">
          <div class="tio-row-tight">
            <span class="text-sm tio-fg-muted">
              {{this.total}}
              results
            </span>
            {{#if this.isNearOffsetLimit}}
              <span
                class="tio-inline-row-extra-tight text-xs tio-fg-warning-body tio-bg-warning-soft rounded-full px-2 py-0.5"
              >
                <PhWarning @size={{12}} />
                {{this.offsetLimitWarning}}
              </span>
            {{/if}}
          </div>
          <div class="tio-row">
            <Select
              @items={{this.limitOptions}}
              @selected={{this.selectedLimitKey}}
              @onChange={{this.handleLimitChange}}
              @size="sm"
              @clearable={{false}}
            />
            <div class="tio-row-extra-tight">
              <Button
                @appearance="outlined"
                @size="sm"
                @icon={{PhCaretLeft}}
                @isDisabled={{not this.canGoBack}}
                {{on "click" this.goToPrevPage}}
              />
              <span class="text-sm tio-fg-secondary px-2">
                {{@page}}
                /
                {{this.effectiveTotalPages}}
              </span>
              <Button
                @appearance="outlined"
                @size="sm"
                @icon={{PhCaretRight}}
                @isDisabled={{not this.canGoForward}}
                {{on "click" this.goToNextPage}}
              />
            </div>
          </div>
        </div>
      {{else}}
        <div class="tio-row-center h-48 tio-fg-muted text-sm">
          No columns selected. Add fields to build your report.
        </div>
      {{/if}}

      {{#if this.sortActionMenuKey}}
        <div data-sort-action-menu style={{this.sortMenuStyle}} class="app-popover w-40 py-1">
          <button
            type="button"
            class="app-popover-option app-popover-option--sm"
            {{on "click" (fn this.replaceSort this.sortActionMenuKey)}}
          >
            Replace sort
          </button>
          <button
            type="button"
            class="app-popover-option app-popover-option--sm"
            {{on "click" (fn this.addToSort this.sortActionMenuKey)}}
          >
            Add to sort
          </button>
          <button
            type="button"
            class="app-popover-option app-popover-option--sm border-t tio-border-subtle"
            {{on "click" this.dismissSortActionMenu}}
          >
            Cancel
          </button>
        </div>
      {{/if}}
    </div>
  </template>
}

export default PreviewTable;
